import Button from '@common/component//atoms/Button';
import Textbox from '@common/component//atoms/Textbox';
import LabelWithSymbols from '@common/component/atoms/LabelBySymbols';
import Form from '@common/component/molecules/Form';
import { proxyLoginAgencyApi } from '@common/external/ProxyLoginAgencyApi';
import { validHankaku, validNumber, validRequired, validEqualSize } from '@common/utils/ValidationCheck';
import React, { useEffect, useRef, useState } from 'react';
import { RouteComponentProps, useHistory, withRouter } from 'react-router-dom';
import { redirectSorryPage } from '../../../container/templates/AuthContainer';
import { initDataLayer } from '@common/utils/DataLayer';

const ProxyLogin: React.FC<RouteComponentProps> = () => {
  const url = new URL(decodeURIComponent(window.location.href));
  const params = url.searchParams;
  const agencyCdOrigin = params.get('agencyCd') || '';
  const crewCdOrigin = params.get('crewCd') || '';
  const token = params.get('token') || '';

  const agencyCdRef = useRef<HTMLInputElement>(null);
  const crewCdRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [agencyCd, setAgencyCd] = useState<string>('');
  const [isAgencyCdError, setIsAgencyCdError] = useState<boolean>(false);
  const [agenCdErrorMessage, setAgencyCdErrorMessage] = useState<string>('');
  const [crewCd, setCrewCd] = useState<string>('');
  const [isCrewCdError, setIsCrewCdError] = useState<boolean>(false);
  const [crewCdErrorMessage, setCrewCdErrorMessage] = useState<string>('');
  const [errMessage, setErrMessage] = useState<string>('');
  const history = useHistory();

  const requiredErrorMessageCrew = validRequired(crewCd);
  const sizeErrorMessageCrew = validEqualSize(crewCd, 10);
  const validNumberMesagesCrew = validNumber(crewCd);

  const requiredErrorMessageAgency = validRequired(agencyCd);
  const sizeErrorMessageAgency = validEqualSize(agencyCd, 9);
  const validHankakuMesagesAgency = validHankaku(agencyCd);

  const onBlurCrewCd = () => {
    if (validNumberMesagesCrew) {
      setIsCrewCdError(true);
      setCrewCdErrorMessage(validNumberMesagesCrew);
      return;
    }
    if (sizeErrorMessageCrew) {
      setIsCrewCdError(true);
      setCrewCdErrorMessage(sizeErrorMessageCrew);
      return;
    }
    if (requiredErrorMessageCrew) {
      setIsCrewCdError(true);
      setCrewCdErrorMessage(requiredErrorMessageCrew);
      return;
    }
    setIsCrewCdError(false);
    setCrewCdErrorMessage('');
  };

  const onBlurAgencyCd = () => {
    if (validHankakuMesagesAgency) {
      setIsAgencyCdError(true);
      setAgencyCdErrorMessage(validHankakuMesagesAgency);
      return;
    }
    if (sizeErrorMessageAgency) {
      setIsAgencyCdError(true);
      setAgencyCdErrorMessage(sizeErrorMessageAgency);
      return;
    }
    if (requiredErrorMessageAgency) {
      setIsAgencyCdError(true);
      setAgencyCdErrorMessage(requiredErrorMessageAgency);
      return;
    }
    setIsAgencyCdError(false);
    setAgencyCdErrorMessage('');
  };

  useEffect(() => {
    if (isCrewCdError) onBlurCrewCd();
    // eslint-disable-next-line
  }, [crewCd]);

  useEffect(() => {
    if (isAgencyCdError) onBlurAgencyCd();
    // eslint-disable-next-line
  }, [agencyCd]);

  const proxyLogin = async () => {
    if (requiredErrorMessageAgency) {
      setIsAgencyCdError(true);
      setAgencyCdErrorMessage(requiredErrorMessageAgency);
      agencyCdRef.current?.focus();
    } else if (requiredErrorMessageCrew) {
      setIsCrewCdError(true);
      setCrewCdErrorMessage(requiredErrorMessageCrew);
      crewCdRef.current?.focus();
    } else if (agenCdErrorMessage === '' && crewCdErrorMessage === '') {
      setLoading(true);
      await proxyLoginAgencyApi({
        crewCd: crewCd,
        agencyCd: agencyCd,
        agencyCdOrigin: agencyCdOrigin,
        crewCdOrigin: crewCdOrigin,
        token: token,
      })
        .then((data) => {
          if (data.result) {
            if (data.result?.resultCd === '400') {
              setErrMessage(data.result.resultMessageDtoList?.[0]?.resultMessage ?? '');
            } else redirectSorryPage(history);
          } else window.location.replace(data.toString());
        })
        .catch(() => {
          redirectSorryPage(history);
        });
      setLoading(false);
    }
  };

  useEffect(() => {
    initDataLayer({
      crewCd: '',
      agencyCd: '',
      employeeNumber: crewCdOrigin,
    });
  }, []); // eslint-disable-line

  return (
    <div className='u-flex layout-center'>
      <div className='u-text-center'>
        <h1 className='u-text-primary u-text-bold u-text-2l u-flex u-justify-center u-align-center'>
          <img className='uc-sidenav__left-icon' src={`${process.env.PUBLIC_URL}/mikomi-icon.svg`} alt='icon' width='30' />
          <div className='u-font-meiryo u-icon-m u-pl4'>見込顧客管理</div>
        </h1>
        <LabelWithSymbols
          className='u-p16 uc-file-uploader__explain'
          text={'代理ログインする場合は<br>ログイン情報を変更してください'}
          data-testid='proxy-login--message'
        />
        {errMessage && (
          <div className='uc-form-field__message uc-form-field__message--error u-pb8'>
            <span className='material-icons MuiIcon-root proxy-login-error--message' aria-hidden='true'>
              error
            </span>
            <span data-testid='proxy-login-error--message'>{errMessage}</span>
          </div>
        )}
        <Form onSubmit={proxyLogin}>
          <div className='u-pb16 uc-form-field__label--min-wml'>
            <Form.FileldChild
              labelFor='txt_last_name'
              className='u-mt4 u-text-left'
              label='取次店コード'
              error={isAgencyCdError}
              data-testid='proxy-login--agency-cd-container'
              message={agenCdErrorMessage}>
              <Textbox
                id='id'
                width='full'
                data-testid='proxy-login--agency-cd'
                height='s'
                onChange={(e: any) => {
                  setErrMessage('');
                  setAgencyCd(e.target.value);
                }}
                onBlur={(e: any) => {
                  setAgencyCd(e.target.value);
                  onBlurAgencyCd();
                }}
                inputRef={agencyCdRef}
                value={agencyCd}
              />
            </Form.FileldChild>
          </div>
          <div className='u-pb32'>
            <Form.FileldChild
              labelFor='txt_last_name'
              className='u-mt4 u-text-left'
              label='クルーコード'
              error={isCrewCdError}
              data-testid='proxy-login--crew-cd-container'
              message={crewCdErrorMessage}>
              <Textbox
                id='id'
                wrapperClass='filter-modal__textbox u-w-full'
                data-testid='proxy-login--crew-cd'
                width='full'
                height='s'
                onChange={(e: any) => {
                  setErrMessage('');
                  setCrewCd(e.target.value);
                }}
                inputRef={crewCdRef}
                value={crewCd}
                onBlur={(e: any) => {
                  setCrewCd(e.target.value);
                  onBlurCrewCd();
                }}
              />
            </Form.FileldChild>
          </div>
          <Button width='full' label='ログイン' data-testid='proxy-login--login' onClick={proxyLogin} disabled={loading} />
        </Form>
      </div>
    </div>
  );
};

export default withRouter(ProxyLogin);
